import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Button,
    Card,
    Group,
    Loader,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
    Textarea,
    Title,
    Badge,
    Paper,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";

const apiUrl = process.env.REACT_APP_API_URL;

type WhatsAppMessageDto = {
    id: number;
    messageId: string;
    phoneNumber: string;
    direction: "inbound" | "outbound";
    type: string;
    textBody?: string | null;
    status?: string | null;
    errorMessage?: string | null;
    createdAtUtc: string;
    sentAtUtc?: string | null;
    deliveredAtUtc?: string | null;
    readAtUtc?: string | null;
};

const WhatsAppAdmin: React.FC = () => {
    const [phoneNumber, setPhoneNumber] = useState("");
    const [message, setMessage] = useState("");
    const [messages, setMessages] = useState<WhatsAppMessageDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [filter, setFilter] = useState<string>("all");

    const loadMessages = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${apiUrl}/api/whatsapp/messages`);
            setMessages(res.data);
        } catch (error) {
            showNotification({
                title: "Fejl",
                message: "Kunne ikke hente WhatsApp-beskeder",
                color: "red",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadMessages();
    }, []);

    const sendMessage = async () => {
        if (!phoneNumber.trim()) {
            showNotification({
                title: "Manglende nummer",
                message: "Indtast et telefonnummer",
                color: "yellow",
            });
            return;
        }

        if (!message.trim()) {
            showNotification({
                title: "Manglende besked",
                message: "Skriv en besked",
                color: "yellow",
            });
            return;
        }

        try {
            setSending(true);

            await axios.post(`${apiUrl}/api/whatsapp/send`, {
                phoneNumber,
                message,
            });

            showNotification({
                title: "Sendt",
                message: "Beskeden blev sendt",
                color: "green",
            });

            setMessage("");
            await loadMessages();
        } catch (error: any) {
            showNotification({
                title: "Fejl ved sending",
                message:
                    error?.response?.data?.message ||
                    error?.response?.data ||
                    "Beskeden kunne ikke sendes",
                color: "red",
            });
        } finally {
            setSending(false);
        }
    };

    const filteredMessages = useMemo(() => {
        if (filter === "inbound") {
            return messages.filter((x) => x.direction === "inbound");
        }

        if (filter === "outbound") {
            return messages.filter((x) => x.direction === "outbound");
        }

        return messages;
    }, [messages, filter]);

    const rows = filteredMessages.map((msg) => (
        <Table.Tr key={msg.id}>
            <Table.Td>
                <Badge color={msg.direction === "inbound" ? "blue" : "green"}>
                    {msg.direction === "inbound" ? "Ind" : "Ud"}
                </Badge>
            </Table.Td>
            <Table.Td>{msg.phoneNumber}</Table.Td>
            <Table.Td>{msg.textBody || "-"}</Table.Td>
            <Table.Td>{msg.status || "-"}</Table.Td>
            <Table.Td>{msg.type}</Table.Td>
            <Table.Td>
                {new Date(msg.createdAtUtc).toLocaleString("da-DK")}
            </Table.Td>
            <Table.Td>{msg.errorMessage || "-"}</Table.Td>
        </Table.Tr>
    ));

    return (
        <Stack gap="lg">
            <Title order={2}>WhatsApp testcenter</Title>

            <Card withBorder radius="md" padding="lg">
                <Stack gap="md">
                    <Title order={4}>Send testbesked</Title>

                    <TextInput
                        label="Telefonnummer"
                        placeholder="+4512345678"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.currentTarget.value)}
                    />

                    <Textarea
                        label="Besked"
                        placeholder="Skriv din besked her..."
                        minRows={5}
                        value={message}
                        onChange={(e) => setMessage(e.currentTarget.value)}
                    />

                    <Group justify="space-between">
                        <Button variant="default" onClick={loadMessages} loading={loading}>
                            Opdater liste
                        </Button>

                        <Button onClick={sendMessage} loading={sending}>
                            Send besked
                        </Button>
                    </Group>
                </Stack>
            </Card>

            <Paper withBorder radius="md" p="lg">
                <Stack gap="md">
                    <Group justify="space-between">
                        <Title order={4}>Beskedoversigt</Title>

                        <Select
                            w={180}
                            value={filter}
                            onChange={(value) => setFilter(value || "all")}
                            data={[
                                { value: "all", label: "Alle" },
                                { value: "inbound", label: "Kun indgående" },
                                { value: "outbound", label: "Kun udgående" },
                            ]}
                        />
                    </Group>

                    {loading ? (
                        <Group justify="center" py="xl">
                            <Loader />
                        </Group>
                    ) : filteredMessages.length === 0 ? (
                        <Text c="dimmed">Ingen beskeder fundet.</Text>
                    ) : (
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Retning</Table.Th>
                                    <Table.Th>Nummer</Table.Th>
                                    <Table.Th>Besked</Table.Th>
                                    <Table.Th>Status</Table.Th>
                                    <Table.Th>Type</Table.Th>
                                    <Table.Th>Oprettet</Table.Th>
                                    <Table.Th>Fejl</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>{rows}</Table.Tbody>
                        </Table>
                    )}
                </Stack>
            </Paper>
        </Stack>
    );
};

export default WhatsAppAdmin;